import { AssetHealthQueryParams, useGetHealthAssetsQuery } from '@customer/store/apis/healthApi';
import styles from './Summary.module.scss';
import { useAppSelector } from '@customer/store/hooks';
import AssetHealthCard from './components/asset-health-card/AssetHealthCard';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useRef } from 'react';
import { FaultLevel } from '@customer/store/slices/globalSlice';
import GlobalTimeRangeFilter from '@customer/components/inputs/global-time-range/GlobalTimeRangeFilter';
import { getVdsSpacing } from '@cas/utils/vdsHelpers';
import { NoResults } from '@dmc/ui';
import FilterButton from '@customer/components/inputs/filter-button/FilterButton';
import FaultLevelTabs from './components/fault-level-tabs/FaultLevelTabs';
import FilterSummary from '@customer/components/inputs/filter-summary/FilterSummary';
import { FilterGroup } from '@customer/filters/types';
import useFilters from '@customer/filters/useFilters';

const ESTIMATED_CARD_HEIGHT = 94;

export function Summary() {
  const { t } = useTranslation();

  const userData = useAppSelector(state => state.auth);
  const timeRange = useAppSelector(state => state.global.timeRange);
  const faultLevel = useAppSelector(state => state.health.faultLevel);

  const filters = useFilters<AssetHealthQueryParams>(FilterGroup.HealthSummary);

  const {
    data: assets,
    isLoading,
    isFetching,
  } = useGetHealthAssetsQuery(
    {
      query: {
        ...filters,
        timeRange,
        faultLevel,
      },
    },
    { refetchOnMountOrArgChange: 60, skip: userData.siteId === undefined },
  );

  const allAssets = assets?.assets ?? [];
  const assetsCount = assets?.assets?.length ?? 0; // Fallback to skeleton cards

  const parentRef = useRef(null);

  const rowVirtualizer = useVirtualizer({
    count: assetsCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ESTIMATED_CARD_HEIGHT + getVdsSpacing(4), // card height + bottom padding
    overscan: 8,
  });

  const responseFaultLevel = Number(assets?.level ?? FaultLevel.One);

  return (
    <div className={styles.container}>
      <div className={styles.actions}>
        <div className={styles.actions__results}>
          <FilterSummary
            transKey="ShowingAssetsSortedByActiveFaults"
            countAll={assets?.assets?.length}
            filterGroup={FilterGroup.HealthSummary}
          />
        </div>

        <FaultLevelTabs />

        <div className={styles.actions__timeRange}>
          <GlobalTimeRangeFilter disabled={isFetching || userData.siteChanging} />
        </div>

        <div className={styles.actions__buttons}>
          <FilterButton
            filters={FilterGroup.HealthSummary}
            disabled={isFetching || userData.siteChanging}
          />
        </div>
      </div>

      {assets && assetsCount > 0 ? (
        <div ref={parentRef} className={styles.cards}>
          {/* Dynamic virtualisation (unknown, estimated row height) */}
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {rowVirtualizer.getVirtualItems().map(virtualItem => {
              return (
                <div
                  key={virtualItem.key}
                  data-index={virtualItem.index}
                  className={styles.rowcard}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  <AssetHealthCard
                    asset={allAssets[virtualItem.index]}
                    faultLevel={responseFaultLevel}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <NoResults
          description={
            isLoading ? t('Loading') : t('NoAssetsWithLevelFaults', { level: faultLevel })
          }
        />
      )}
    </div>
  );
}

export default Summary;
